/**
 * Tab de Análisis: vista densa de gráficas para inspeccionar gastos,
 * ingresos y patrones con 5 modos de rango temporal (anual, trimestral,
 * mensual, semanal, personalizado).
 *
 * Decisión: agrupa por bucket dinámico (día / semana / mes) según el
 * rango total para que el eje X siempre tenga 5-30 puntos visibles.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  Calendar,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  FileText,
} from "lucide-react";
import clsx from "clsx";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart as RPieChart,
  Pie,
  Cell,
} from "recharts";
import { api } from "@/lib/api";
import { formatMxnCents } from "@/lib/format";
import { BurnRateGauge, utilidadPctTone } from "@/features/finance/BurnRateGauge";
import { ChartCard } from "@/features/finance/ChartCard";
import { MonthlyBalancesTable } from "@/features/finance/monthlyBalances";
import { exportFinancePdf } from "@/features/finance/exportPdf";

type RangeMode = "anual" | "trimestral" | "mensual" | "semanal" | "personalizado";

interface AnalysisTxSplit {
  id: string;
  categoryId: string | null;
  projectId: string | null;
  companyId: string | null;
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
}

interface AnalysisTx {
  id: string;
  date: string;
  kind: "ingreso" | "gasto";
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
  accountId: string;
  categoryId: string | null;
  companyId: string | null;
  projectId: string | null;
  paymentMethod: "transfer" | "card_credit" | "card_debit" | "cash" | null;
  invoiceStatus: string;
  documentedExternally?: boolean;
  description: string | null;
  counterparty: string | null;
  documents?: { id: string }[];
  splits?: AnalysisTxSplit[];
}

interface ProjectInfo {
  id: string;
  name: string;
  type: "client" | "internal";
}

interface Account {
  id: string;
  name: string;
}

interface Category {
  id: string;
  name: string;
  group?: string | null;
}

const fmtMx = (v: number) => v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });

const COLORS = [
  "oklch(0.78 0.16 145)", // green
  "oklch(0.65 0.2 25)", // red
  "oklch(0.62 0.18 220)", // blue
  "oklch(0.72 0.18 60)", // orange
  "oklch(0.65 0.2 320)", // purple
  "oklch(0.78 0.16 180)", // teal
  "oklch(0.7 0.18 100)", // yellow-green
  "oklch(0.62 0.2 0)", // pink
];

// Paleta y hash estable para colores por proveedor (mismo color
// independiente del orden/rango). Comparte la idea con SuppliersTab.
const SUPPLIER_PALETTE = [
  "oklch(0.65 0.2 25)",
  "oklch(0.72 0.18 60)",
  "oklch(0.7 0.18 100)",
  "oklch(0.78 0.16 145)",
  "oklch(0.78 0.16 180)",
  "oklch(0.62 0.18 220)",
  "oklch(0.65 0.2 270)",
  "oklch(0.65 0.2 320)",
  "oklch(0.62 0.2 0)",
  "oklch(0.55 0.15 30)",
];

/**
 * Hook local para que cada gráfica pueda ocultar series al click en su
 * leyenda. Mantiene un Set de keys ocultas; cada chart instancia uno.
 */
function useHiddenSeries() {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const toggle = (key: unknown) => {
    if (key === undefined || key === null) return;
    const k = typeof key === "function" ? String(key) : String(key);
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  return {
    hidden,
    toggle,
    isHidden: (k: string | number) => hidden.has(String(k)),
  };
}

/**
 * Formatter de Legend que tacha y baja opacidad cuando la serie está
 * oculta. Se llama por entry; reusable entre charts. La firma se ensancha
 * para encajar con el tipo `Formatter` de recharts (dataKey puede ser una
 * función accessor).
 */
function legendFormatter(isHidden: (k: string) => boolean) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (value: any, entry: any) => {
    const raw = entry?.dataKey ?? value;
    const key = typeof raw === "string" || typeof raw === "number" ? String(raw) : String(value);
    const hidden = isHidden(key);
    return (
      <span
        style={{
          textDecoration: hidden ? "line-through" : undefined,
          opacity: hidden ? 0.5 : 1,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {value}
      </span>
    );
  };
}

function supplierColor(name: string): string {
  if (name === "Sin proveedor") return "oklch(0.6 0.02 280)";
  if (name === "Otros") return "oklch(0.7 0.02 280)";
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const idx = name.length === 0 ? 0 : h % SUPPLIER_PALETTE.length;
  return SUPPLIER_PALETTE[idx] ?? SUPPLIER_PALETTE[0]!;
}

export function AnalysisTab({ period }: { period: string }) {
  const [mode, setMode] = useState<RangeMode>("mensual");
  const [anchor, setAnchor] = useState<string>(period); // YYYY-MM-DD anchor
  // Toggle "expandir splits" compartido vía localStorage con las otras tabs.
  const [expandSplits, setExpandSplits] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("axorial.tx.expandSplits") === "1";
  });
  const [customFrom, setCustomFrom] = useState<string>(period + "-01");
  const [customTo, setCustomTo] = useState<string>(() => {
    const [y, m] = period.split("-").map(Number);
    const last = new Date(y!, m!, 0).getDate();
    return `${period}-${String(last).padStart(2, "0")}`;
  });

  // Calcular from/to según el modo + anchor.
  const { from, to, label } = useMemo(() => {
    const a = parseAnchor(anchor, mode);
    return resolveRange(mode, a, customFrom, customTo);
  }, [mode, anchor, customFrom, customTo]);

  // Export PDF — el reporte branded del rango actual.
  const [exportingPdf, setExportingPdf] = useState(false);
  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      // Pull tx con todos los campos (AnalysisTx no trae paymentDate,
      // companyName, title, etc. que el PDF aprovecha).
      const txsRes = await api.get<{
        transactions: (import("@axorial/shared").Transaction & { companyName: string | null })[];
      }>(`/finance/transactions?from=${from}&to=${to}&affectsTotals=1`);
      const periodGuess = from.slice(0, 7);
      // Reusa los mapas ya cargados en pantalla.
      const accountNameById = new Map(accounts.data?.accounts.map((a) => [a.id, a.name]) ?? []);
      const categoryNameById = new Map(
        categories.data?.categories.map((c) => [c.id, c.name]) ?? [],
      );
      await exportFinancePdf({
        transactions: txsRes.transactions,
        period: from === `${periodGuess}-01` ? periodGuess : null,
        accountById: accountNameById,
        categoryById: categoryNameById,
      });
    } finally {
      setExportingPdf(false);
    }
  };

  const shiftAnchor = (dir: -1 | 1) => {
    const a = parseAnchor(anchor, mode);
    const next = new Date(a);
    if (mode === "anual") next.setFullYear(a.getFullYear() + dir);
    else if (mode === "trimestral") next.setMonth(a.getMonth() + dir * 3);
    else if (mode === "mensual") next.setMonth(a.getMonth() + dir);
    else if (mode === "semanal") next.setDate(a.getDate() + dir * 7);
    setAnchor(
      `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`,
    );
  };
  const shiftPrev = () => shiftAnchor(-1);
  const shiftNext = () => shiftAnchor(1);

  const txs = useQuery<{ transactions: AnalysisTx[] }>({
    queryKey: ["finance", "analysis", from, to],
    queryFn: () => api.get(`/finance/transactions?from=${from}&to=${to}&affectsTotals=1`),
  });
  const accounts = useQuery<{ accounts: Account[] }>({
    queryKey: ["finance", "accounts"],
    queryFn: () => api.get("/finance/accounts"),
  });
  const suppliers = useQuery<{ companies: { id: string; name: string }[] }>({
    queryKey: ["companies", "suppliers"],
    queryFn: () => api.get("/companies?isSupplier=1"),
  });
  const allCompanies = useQuery<{ companies: { id: string; name: string }[] }>({
    queryKey: ["companies"],
    queryFn: () => api.get("/companies"),
    staleTime: 5 * 60_000,
  });
  const categories = useQuery<{ categories: Category[] }>({
    queryKey: ["finance", "categories"],
    queryFn: () => api.get("/finance/categories"),
  });
  const projects = useQuery<{ projects: ProjectInfo[] }>({
    queryKey: ["projects", "analysis"],
    queryFn: () => api.get("/projects?status=active,paused,completed"),
  });

  // Rango previo equivalente: misma duración pero terminando el día antes
  // del rango actual. Sirve para los KPI deltas "vs periodo anterior".
  const prevRange = useMemo(() => {
    const fromMs = new Date(from + "T00:00:00").getTime();
    const toMs = new Date(to + "T00:00:00").getTime();
    const days = Math.round((toMs - fromMs) / 86400_000) + 1;
    const prevToMs = fromMs - 86400_000;
    const prevFromMs = prevToMs - (days - 1) * 86400_000;
    const fmt = (ms: number) => {
      const d = new Date(ms);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    return { from: fmt(prevFromMs), to: fmt(prevToMs) };
  }, [from, to]);

  const prevTxs = useQuery<{ transactions: AnalysisTx[] }>({
    queryKey: ["finance", "analysis-prev", prevRange.from, prevRange.to],
    queryFn: () =>
      api.get(`/finance/transactions?from=${prevRange.from}&to=${prevRange.to}&affectsTotals=1`),
  });

  const rawList = txs.data?.transactions ?? [];
  const accountById = new Map(accounts.data?.accounts.map((a) => [a.id, a.name]) ?? []);
  const categoryById = new Map(categories.data?.categories.map((c) => [c.id, c]) ?? []);

  // Cuando expandSplits=ON y la tx tiene splits, sustituimos la tx por sus
  // N pedazos (con override de categoría/proyecto/empresa). Los pedazos
  // heredan fecha/cuenta/kind del padre para que los breakdowns por cuenta
  // o las tendencias temporales sigan teniendo sentido.
  const list = useMemo<AnalysisTx[]>(() => {
    if (!expandSplits) return rawList;
    const out: AnalysisTx[] = [];
    for (const t of rawList) {
      if (!t.splits || t.splits.length === 0) {
        out.push(t);
        continue;
      }
      for (const s of t.splits) {
        out.push({
          ...t,
          // El id del split para no colisionar con el padre.
          id: s.id,
          subtotalCents: s.subtotalCents,
          ivaCents: s.ivaCents,
          totalCents: s.totalCents,
          categoryId: s.categoryId ?? t.categoryId,
          companyId: s.companyId ?? t.companyId,
        });
      }
    }
    return out;
  }, [rawList, expandSplits]);

  // KPIs totales del rango. Convención: el monto grande es el NETO
  // (subtotal sin IVA). El IVA va aparte para reportar el SAT.
  const totals = useMemo(() => {
    let ingNeto = 0,
      gasNeto = 0,
      ivaCobrado = 0,
      ivaPagado = 0;
    for (const t of list) {
      if (t.kind === "ingreso") {
        ingNeto += t.subtotalCents;
        ivaCobrado += t.ivaCents;
      } else if (t.kind === "gasto") {
        gasNeto += t.subtotalCents;
        ivaPagado += t.ivaCents;
      }
    }
    return {
      ingresosNetoCents: ingNeto,
      gastosNetoCents: gasNeto,
      ivaCobradoCents: ivaCobrado,
      ivaPagadoCents: ivaPagado,
      // Margen siempre se reporta en neto para no contaminar con flujo IVA.
      margenCents: ingNeto - gasNeto,
      ivaNetoCents: ivaCobrado - ivaPagado,
      count: list.length,
    };
  }, [list]);

  // Agregación por bucket temporal (día/semana/mes según rango).
  const bucketed = useMemo(() => bucketByPeriod(list, from, to), [list, from, to]);

  // Top gastos / ingresos por contraparte. Pedimos 50 para alimentar el
  // modal "Ver todos" sin re-iterar; el card resumen solo muestra los 5.
  const topGastos = useMemo(() => topBy(list, "gasto", 50), [list]);
  const topIngresos = useMemo(() => topBy(list, "ingreso", 50), [list]);
  const [seeAllKind, setSeeAllKind] = useState<"gasto" | "ingreso" | null>(null);

  // Distribución por categoría (solo gastos). Track neto + IVA por separado
  // para mantener la convención: el slice del donut y el monto grande son el
  // NETO; el IVA se muestra aparte con su bruto resultante.
  const byCategory = useMemo(() => {
    const map = new Map<string, { sub: number; iva: number }>();
    for (const t of list) {
      if (t.kind !== "gasto") continue;
      const name = t.categoryId
        ? (categoryById.get(t.categoryId)?.name ?? "Sin categoría")
        : "Sin categoría";
      const cur = map.get(name) ?? { sub: 0, iva: 0 };
      cur.sub += t.subtotalCents;
      cur.iva += t.ivaCents;
      map.set(name, cur);
    }
    return [...map.entries()]
      .map(([name, v]) => ({
        name,
        // value en pesos (para Recharts dataKey legacy) y *Cents para format.
        value: v.sub / 100,
        subtotalCents: v.sub,
        ivaCents: v.iva,
        totalCents: v.sub + v.iva,
      }))
      .toSorted((a, b) => b.subtotalCents - a.subtotalCents)
      .slice(0, 10);
  }, [list, categoryById]);

  // Proveedores: ranking total + tendencia del top 5 + distribución %.
  // Solo gastos con companyId; los sin tag caen en "Sin proveedor".
  const supplierById = useMemo(
    () => new Map((suppliers.data?.companies ?? []).map((s) => [s.id, s.name])),
    [suppliers.data],
  );
  const supplierTotals = useMemo(() => {
    // Track neto + iva por proveedor para mostrar desglose fiscal en charts.
    const map = new Map<string, { sub: number; iva: number }>();
    let untaggedSub = 0;
    let untaggedIva = 0;
    for (const t of list) {
      if (t.kind !== "gasto") continue;
      if (t.companyId) {
        const name = supplierById.get(t.companyId) ?? "Proveedor eliminado";
        const cur = map.get(name) ?? { sub: 0, iva: 0 };
        cur.sub += t.subtotalCents;
        cur.iva += t.ivaCents;
        map.set(name, cur);
      } else {
        untaggedSub += t.subtotalCents;
        untaggedIva += t.ivaCents;
      }
    }
    const totalSub = [...map.values()].reduce((a, b) => a + b.sub, 0) + untaggedSub;
    const items = [...map.entries()]
      .map(([name, v]) => ({
        name,
        subtotalCents: v.sub,
        ivaCents: v.iva,
        totalCents: v.sub + v.iva,
        // value (pesos, neto) por compat con paths que aún lo lean.
        value: v.sub / 100,
        pct: totalSub > 0 ? (v.sub / totalSub) * 100 : 0,
      }))
      .toSorted((a, b) => b.subtotalCents - a.subtotalCents);
    return {
      items,
      untaggedSubCents: untaggedSub,
      untaggedIvaCents: untaggedIva,
      totalSubCents: totalSub,
    };
  }, [list, supplierById]);

  // Distribución pie: top 6 + Otros + Sin proveedor.
  const supplierDistribution = useMemo(() => {
    const items = supplierTotals.items;
    if (items.length === 0 && supplierTotals.untaggedSubCents === 0) return [];
    const top = items.slice(0, 6);
    const restSub = items.slice(6).reduce((s, x) => s + x.subtotalCents, 0);
    const restIva = items.slice(6).reduce((s, x) => s + x.ivaCents, 0);
    const out: Array<{
      name: string;
      subtotalCents: number;
      ivaCents: number;
      totalCents: number;
    }> = top.map((x) => ({
      name: x.name,
      subtotalCents: x.subtotalCents,
      ivaCents: x.ivaCents,
      totalCents: x.totalCents,
    }));
    if (restSub > 0)
      out.push({
        name: "Otros",
        subtotalCents: restSub,
        ivaCents: restIva,
        totalCents: restSub + restIva,
      });
    if (supplierTotals.untaggedSubCents > 0)
      out.push({
        name: "Sin proveedor",
        subtotalCents: supplierTotals.untaggedSubCents,
        ivaCents: supplierTotals.untaggedIvaCents,
        totalCents: supplierTotals.untaggedSubCents + supplierTotals.untaggedIvaCents,
      });
    return out;
  }, [supplierTotals]);

  // (Antes había un useMemo `supplierTrend` para la card "Tendencia top 5
  // proveedores" pero esa card se removió de la vista — el dato seguía
  // calculándose en vano. Si se requiere de nuevo, recuperarlo del git
  // history en este archivo.)

  // Por cuenta (ingresos y gastos). Trackeamos neto + IVA por separado
  // para apilar el IVA como sub-barra encima del neto en el chart.
  const byAccount = useMemo(() => {
    type Bucket = {
      ingresos: number;
      ingresosIva: number;
      gastos: number;
      gastosIva: number;
    };
    const map = new Map<string, Bucket>();
    for (const t of list) {
      const name = accountById.get(t.accountId) ?? "—";
      const cur = map.get(name) ?? { ingresos: 0, ingresosIva: 0, gastos: 0, gastosIva: 0 };
      if (t.kind === "ingreso") {
        cur.ingresos += t.subtotalCents / 100;
        cur.ingresosIva += t.ivaCents / 100;
      } else if (t.kind === "gasto") {
        cur.gastos += t.subtotalCents / 100;
        cur.gastosIva += t.ivaCents / 100;
      }
      map.set(name, cur);
    }
    return [...map.entries()]
      .map(([name, v]) => ({ name, ...v }))
      .toSorted(
        (a, b) =>
          b.gastos +
          b.gastosIva +
          b.ingresos +
          b.ingresosIva -
          (a.gastos + a.gastosIva + a.ingresos + a.ingresosIva),
      );
  }, [list, accountById]);

  // Hidden state por gráfica (clic en leyenda oculta esa serie).
  const trendHidden = useHiddenSeries();
  const accountHidden = useHiddenSeries();
  const categoryHidden = useHiddenSeries();
  const supplierDistHidden = useHiddenSeries();
  const paymentMethodHidden = useHiddenSeries();
  const histogramHidden = useHiddenSeries();

  // KPI deltas vs periodo previo (mismo número de días, terminando el día
  // antes del rango actual).
  const prevTotals = useMemo(() => {
    let ingNeto = 0,
      gasNeto = 0;
    for (const t of prevTxs.data?.transactions ?? []) {
      if (t.kind === "ingreso") ingNeto += t.subtotalCents;
      else if (t.kind === "gasto") gasNeto += t.subtotalCents;
    }
    return {
      ingresosNetoCents: ingNeto,
      gastosNetoCents: gasNeto,
      margenCents: ingNeto - gasNeto,
    };
  }, [prevTxs.data]);

  // Margen por proyecto: agrupa ingresos − gastos por proyecto. Solo
  // muestra los que tienen movimiento. Sin proyecto = "Axorial (general)".
  const projectById = useMemo(
    () => new Map((projects.data?.projects ?? []).map((p) => [p.id, p])),
    [projects.data],
  );
  const marginByProject = useMemo(() => {
    const map = new Map<string, { name: string; ingresos: number; gastos: number }>();
    const ungrouped = { name: "Axorial (general)", ingresos: 0, gastos: 0 };
    for (const t of list) {
      const proj = t.projectId ? projectById.get(t.projectId) : null;
      const bucket = proj
        ? (map.get(proj.id) ??
          (() => {
            const entry = { name: proj.name, ingresos: 0, gastos: 0 };
            map.set(proj.id, entry);
            return entry;
          })())
        : ungrouped;
      if (t.kind === "ingreso") bucket.ingresos += t.subtotalCents;
      else if (t.kind === "gasto") bucket.gastos += t.subtotalCents;
    }
    const out = [...map.values()];
    if (ungrouped.ingresos > 0 || ungrouped.gastos > 0) out.push(ungrouped);
    return out
      .map((b) => ({
        name: b.name,
        ingresosCents: b.ingresos,
        gastosCents: b.gastos,
        margenCents: b.ingresos - b.gastos,
      }))
      .toSorted((a, b) => b.margenCents - a.margenCents);
  }, [list, projectById]);

  // Cumplimiento de facturación: % de txs que requieren CFDI y lo tienen
  // (subido o declarado externo). Excluye no_aplica/olvidado y transfers.
  const cfdiCompliance = useMemo(() => {
    let required = 0;
    let fulfilled = 0;
    let missing = 0;
    for (const t of rawList) {
      if (t.invoiceStatus === "no_aplica" || t.invoiceStatus === "olvidado") continue;
      required += 1;
      const hasDoc = (t.documents?.length ?? 0) > 0;
      if (hasDoc || t.documentedExternally) fulfilled += 1;
      else missing += 1;
    }
    return {
      required,
      fulfilled,
      missing,
      pct: required > 0 ? (fulfilled / required) * 100 : 100,
    };
  }, [rawList]);

  // Margen por cliente: agrupa por companyId del ingreso, restando gastos
  // del mismo cliente. Útil cuando un cliente paga pero tiene gastos
  // atribuibles (proyecto interno costoso, etc.).
  const marginByClient = useMemo(() => {
    const map = new Map<string, { name: string; ingresos: number; gastos: number }>();
    for (const t of list) {
      if (!t.companyId) continue;
      const cur =
        map.get(t.companyId) ??
        (() => {
          const entry = { name: t.companyId, ingresos: 0, gastos: 0 };
          map.set(t.companyId, entry);
          return entry;
        })();
      if (t.kind === "ingreso") cur.ingresos += t.subtotalCents;
      else if (t.kind === "gasto") cur.gastos += t.subtotalCents;
    }
    // Resolver nombres desde la query de suppliers (que incluye todos —
    // proveedores y clientes — porque sin filtro trae todo).
    const companyName = new Map<string, string>();
    for (const s of allCompanies.data?.companies ?? []) companyName.set(s.id, s.name);
    return [...map.entries()]
      .map(([id, v]) => ({
        id,
        name: companyName.get(id) ?? id,
        ingresosCents: v.ingresos,
        gastosCents: v.gastos,
        margenCents: v.ingresos - v.gastos,
      }))
      .filter((x) => x.ingresosCents > 0)
      .toSorted((a, b) => b.margenCents - a.margenCents)
      .slice(0, 10);
  }, [list, allCompanies.data]);

  // Gastos por método de pago (donut). Mapeo a labels español.
  const byPaymentMethod = useMemo(() => {
    const LABELS: Record<string, string> = {
      transfer: "Transferencia",
      card_credit: "Tarjeta crédito",
      card_debit: "Tarjeta débito",
      cash: "Efectivo",
    };
    const map = new Map<string, number>();
    for (const t of list) {
      if (t.kind !== "gasto") continue;
      const key = t.paymentMethod ? (LABELS[t.paymentMethod] ?? t.paymentMethod) : "Sin método";
      map.set(key, (map.get(key) ?? 0) + t.subtotalCents);
    }
    return [...map.entries()]
      .map(([name, cents]) => ({ name, value: cents }))
      .toSorted((a, b) => b.value - a.value);
  }, [list]);

  // Histograma de tamaño de transacción (subtotal). Buckets logarítmicos
  // para que pequeñas y grandes se vean bien en la misma vista.
  const sizeHistogram = useMemo(() => {
    const BUCKETS = [
      { label: "< $100", maxCents: 10_000 },
      { label: "$100 – $500", maxCents: 50_000 },
      { label: "$500 – $1k", maxCents: 100_000 },
      { label: "$1k – $5k", maxCents: 500_000 },
      { label: "$5k – $20k", maxCents: 2_000_000 },
      { label: "$20k – $100k", maxCents: 10_000_000 },
      { label: "> $100k", maxCents: Infinity },
    ];
    const initial = BUCKETS.map((b) => ({
      label: b.label,
      ingresos: 0,
      gastos: 0,
    }));
    for (const t of list) {
      const cents = t.subtotalCents;
      const idx = BUCKETS.findIndex((b) => cents < b.maxCents);
      const bucket = initial[idx === -1 ? initial.length - 1 : idx]!;
      if (t.kind === "ingreso") bucket.ingresos += 1;
      else if (t.kind === "gasto") bucket.gastos += 1;
    }
    return initial;
  }, [list]);

  return (
    <div className="space-y-6">
      <div className="card-elevated p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-base-200/60 rounded-lg p-1">
          {(["anual", "trimestral", "mensual", "semanal", "personalizado"] as RangeMode[]).map(
            (m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={clsx(
                  "btn btn-xs",
                  mode === m ? "btn-primary" : "btn-ghost text-base-content/65",
                )}
              >
                {m === "anual"
                  ? "Anual"
                  : m === "trimestral"
                    ? "Trimestral"
                    : m === "mensual"
                      ? "Mensual"
                      : m === "semanal"
                        ? "Semanal"
                        : "Personalizado"}
              </button>
            ),
          )}
        </div>

        <label className="cursor-pointer flex items-center gap-2 text-xs text-base-content/75 ml-auto">
          <input
            type="checkbox"
            className="toggle toggle-xs toggle-primary"
            checked={expandSplits}
            onChange={(e) => {
              setExpandSplits(e.target.checked);
              if (typeof window !== "undefined") {
                localStorage.setItem("axorial.tx.expandSplits", e.target.checked ? "1" : "0");
              }
            }}
          />
          <span>Expandir sub-cuentas</span>
        </label>

        <button
          type="button"
          onClick={handleExportPdf}
          disabled={exportingPdf}
          className="btn btn-sm btn-outline btn-primary"
          title={`Descargar PDF branded del reporte · ${label}`}
        >
          {exportingPdf ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <FileText className="size-4" />
          )}
          <span className="hidden sm:inline">PDF</span>
        </button>

        {mode !== "personalizado" ? (
          <div className="flex items-center gap-1">
            <button type="button" onClick={shiftPrev} className="btn btn-ghost btn-sm btn-square">
              <ChevronLeft className="size-4" />
            </button>
            <div className="flex items-center gap-2 text-sm font-medium capitalize px-2">
              <Calendar className="size-4 text-base-content/55" />
              {label}
            </div>
            <button type="button" onClick={shiftNext} className="btn btn-ghost btn-sm btn-square">
              <ChevronRight className="size-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="input input-bordered input-sm"
            />
            <span className="text-base-content/55">→</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="input input-bordered input-sm"
            />
          </div>
        )}
        {mode !== "personalizado" && (
          <button
            type="button"
            onClick={() => setAnchor(todayIso())}
            className="btn btn-ghost btn-xs"
          >
            Hoy
          </button>
        )}
      </div>

      {/* KPIs — monto grande = NETO, IVA + bruto debajo cuando aplica. Mismo
          set y orden que /finance Resumen: 6 tiles, 2×3 en móvil → 6×1 en xl. */}
      {(() => {
        // ISR estimado del rango: 30% sobre max(0, utilidad bruta). Misma
        // fórmula del endpoint isr-summary mensual, replicada en cliente.
        const isrCents = Math.round(Math.max(0, totals.margenCents) * 0.3);
        const utilidadNeta = totals.margenCents - isrCents - totals.ivaNetoCents;
        const pctBruta =
          totals.ingresosNetoCents > 0 ? (totals.margenCents / totals.ingresosNetoCents) * 100 : 0;
        const pctNeta =
          totals.ingresosNetoCents > 0 ? (utilidadNeta / totals.ingresosNetoCents) * 100 : 0;
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3">
            <KpiTile
              label="Ingresos"
              valueCents={totals.ingresosNetoCents + totals.ivaCobradoCents}
              ivaCents={totals.ivaCobradoCents}
              tone="success"
              icon={TrendingUp}
            />
            <KpiTile
              label="Gastos"
              valueCents={totals.gastosNetoCents + totals.ivaPagadoCents}
              ivaCents={totals.ivaPagadoCents}
              tone="error"
              icon={TrendingDown}
            />
            <KpiTile
              label="ISR a pagar"
              valueCents={isrCents}
              tone={isrCents > 0 ? "warning" : "neutral"}
              hint="30% sobre utilidad bruta"
            />
            <KpiTile
              label="IVA neto"
              valueCents={totals.ivaNetoCents}
              tone={totals.ivaNetoCents > 0 ? "warning" : "success"}
              hint={totals.ivaNetoCents > 0 ? "a pagar" : totals.ivaNetoCents < 0 ? "a favor" : "—"}
            />
            <KpiTile
              label="Utilidad bruta"
              valueCents={totals.margenCents}
              tone={totals.margenCents >= 0 ? "success" : "error"}
              pctLabel={totals.ingresosNetoCents > 0 ? `${pctBruta.toFixed(1)}%` : undefined}
              pctTone={totals.ingresosNetoCents > 0 ? utilidadPctTone(pctBruta) : undefined}
              hint="ingresos − gastos"
            />
            <KpiTile
              label="Utilidad neta"
              valueCents={utilidadNeta}
              tone={utilidadNeta >= 0 ? "success" : "error"}
              pctLabel={totals.ingresosNetoCents > 0 ? `${pctNeta.toFixed(1)}%` : undefined}
              pctTone={totals.ingresosNetoCents > 0 ? utilidadPctTone(pctNeta) : undefined}
              hint="bruta − ISR − IVA"
            />
          </div>
        );
      })()}

      {/* Deltas vs periodo previo equivalente (mismo número de días) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <DeltaTile
          label="Δ Ingresos"
          currentCents={totals.ingresosNetoCents}
          previousCents={prevTotals.ingresosNetoCents}
          prevRange={prevRange}
          better="up"
        />
        <DeltaTile
          label="Δ Gastos"
          currentCents={totals.gastosNetoCents}
          previousCents={prevTotals.gastosNetoCents}
          prevRange={prevRange}
          better="down"
        />
        <DeltaTile
          label="Δ Utilidad bruta"
          currentCents={totals.margenCents}
          previousCents={prevTotals.margenCents}
          prevRange={prevRange}
          better="up"
        />
      </div>

      {txs.isLoading ? (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : list.length === 0 ? (
        <div className="card-elevated p-8 text-center text-sm text-base-content/55">
          No hay transacciones en este rango.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Cumplimiento de facturación: gauge horizontal con % cobertura */}
          <CfdiComplianceCard
            required={cfdiCompliance.required}
            fulfilled={cfdiCompliance.fulfilled}
            missing={cfdiCompliance.missing}
            pct={cfdiCompliance.pct}
            className="xl:col-span-2"
          />

          {/* Semáforo del burn rate del rango */}
          <ChartCard
            title="Salud financiera"
            subtitle="Burn rate (gastos / ingresos) · objetivo 70-80%"
            pdfKey="salud-financiera"
          >
            <BurnRateGauge
              ingresosCents={totals.ingresosNetoCents}
              gastosCents={totals.gastosNetoCents}
              isrCents={Math.round(Math.max(0, totals.margenCents) * 0.3)}
              ivaSaldoCents={totals.ivaNetoCents}
            />
          </ChartCard>

          {/* Tendencia (ingresos / gastos / margen acumulado) */}
          <ChartCard
            title="Tendencia"
            subtitle={`Ingresos vs gastos por ${bucketed.bucketLabel} · margen acumulado`}
            pdfKey="tendencia"
          >
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={bucketed.data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => (typeof v === "number" ? fmtMx(v) : String(v))} />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  onClick={(p) => trendHidden.toggle(p.dataKey)}
                  formatter={legendFormatter((k) => trendHidden.isHidden(k))}
                />
                <Bar
                  dataKey="ingresos"
                  name="Ingresos"
                  fill={COLORS[0]}
                  hide={trendHidden.isHidden("ingresos")}
                />
                <Bar
                  dataKey="gastos"
                  name="Gastos"
                  fill={COLORS[1]}
                  hide={trendHidden.isHidden("gastos")}
                />
                <Line
                  dataKey="acumulado"
                  name="Acumulado"
                  stroke={COLORS[2]}
                  strokeWidth={2}
                  dot={false}
                  hide={trendHidden.isHidden("acumulado")}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Por cuenta — neto + IVA apilados (IVA en tono más claro) */}
          <ChartCard title="Por cuenta" subtitle="Ingresos vs gastos · neto + IVA apilado">
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart
                data={byAccount}
                layout="vertical"
                margin={{ top: 8, right: 8, bottom: 8, left: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
                <XAxis
                  type="number"
                  fontSize={11}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <YAxis dataKey="name" type="category" fontSize={11} width={120} />
                <Tooltip formatter={(v) => (typeof v === "number" ? fmtMx(v) : String(v))} />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  onClick={(p) => accountHidden.toggle(p.dataKey)}
                  formatter={legendFormatter((k) => accountHidden.isHidden(k))}
                />
                <Bar
                  dataKey="ingresos"
                  name="Ingresos neto"
                  fill={COLORS[0]}
                  stackId="ingreso"
                  hide={accountHidden.isHidden("ingresos")}
                />
                <Bar
                  dataKey="ingresosIva"
                  name="IVA ingresos"
                  fill={COLORS[0]}
                  fillOpacity={0.4}
                  stackId="ingreso"
                  hide={accountHidden.isHidden("ingresosIva")}
                />
                <Bar
                  dataKey="gastos"
                  name="Gastos neto"
                  fill={COLORS[1]}
                  stackId="gasto"
                  hide={accountHidden.isHidden("gastos")}
                />
                <Bar
                  dataKey="gastosIva"
                  name="IVA gastos"
                  fill={COLORS[1]}
                  fillOpacity={0.4}
                  stackId="gasto"
                  hide={accountHidden.isHidden("gastosIva")}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Saldos por mes"
            subtitle="saldo inicial / final de cada cuenta · computado en vivo"
          >
            <MonthlyBalancesTable accounts={accounts.data?.accounts ?? []} />
          </ChartCard>

          {/* Top movimientos — 5 gastos + 5 ingresos en una sola card */}
          <ChartCard title="Top movimientos" subtitle="por contraparte · neto + IVA">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TopMovementsColumn
                title="Top 5 gastos"
                items={topGastos.slice(0, 5)}
                maxValue={topGastos[0]?.subtotalCents ?? 0}
                tone="error"
                onSeeAll={() => setSeeAllKind("gasto")}
              />
              <TopMovementsColumn
                title="Top 5 ingresos"
                items={topIngresos.slice(0, 5)}
                maxValue={topIngresos[0]?.subtotalCents ?? 0}
                tone="success"
                onSeeAll={() => setSeeAllKind("ingreso")}
              />
            </div>
          </ChartCard>

          {/* Distribución por categoría (donut) */}
          <ChartCard
            title="Gastos por categoría"
            subtitle={`Top ${byCategory.length}`}
            pdfKey="gastos-categoria"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="relative shrink-0 w-full md:w-3/5" style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RPieChart>
                    <Pie
                      data={byCategory.filter((c) => !categoryHidden.isHidden(c.name))}
                      dataKey="subtotalCents"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={100}
                      paddingAngle={2}
                    >
                      {byCategory
                        .filter((c) => !categoryHidden.isHidden(c.name))
                        .map((c) => {
                          const originalIdx = byCategory.findIndex((x) => x.name === c.name);
                          return <Cell key={c.name} fill={COLORS[originalIdx % COLORS.length]} />;
                        })}
                    </Pie>
                    <Tooltip
                      formatter={(v) => (typeof v === "number" ? formatMxnCents(v) : String(v))}
                    />
                  </RPieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                  {(() => {
                    const visible = byCategory.filter((c) => !categoryHidden.isHidden(c.name));
                    const sub = visible.reduce((s, c) => s + c.subtotalCents, 0);
                    const iva = visible.reduce((s, c) => s + c.ivaCents, 0);
                    return (
                      <div className="text-center leading-tight">
                        <div className="text-[9px] uppercase tracking-wider text-base-content/55 font-semibold">
                          Visible
                        </div>
                        <div className="text-sm font-display font-bold tabular-nums whitespace-nowrap">
                          {formatMxnCents(sub)}
                        </div>
                        {iva > 0 && (
                          <div className="text-[9px] text-base-content/45 tabular-nums whitespace-nowrap mt-0.5">
                            +{formatMxnCents(iva)} IVA
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <ul className="flex-1 text-xs space-y-1 max-h-[260px] overflow-y-auto">
                {(() => {
                  const visibleSub = byCategory
                    .filter((c) => !categoryHidden.isHidden(c.name))
                    .reduce((s, c) => s + c.subtotalCents, 0);
                  return byCategory.map((c, i) => {
                    const isHidden = categoryHidden.isHidden(c.name);
                    const pct = visibleSub > 0 ? (c.subtotalCents / visibleSub) * 100 : 0;
                    return (
                      <li key={c.name}>
                        <button
                          type="button"
                          onClick={() => categoryHidden.toggle(c.name)}
                          className="w-full flex items-start gap-2 hover:bg-base-200/50 rounded px-1 py-0.5 text-left cursor-pointer transition-colors"
                          style={{
                            textDecoration: isHidden ? "line-through" : undefined,
                            opacity: isHidden ? 0.45 : 1,
                          }}
                          title={isHidden ? "Mostrar" : "Ocultar del gráfico"}
                        >
                          <span
                            aria-hidden
                            className="size-2.5 rounded-sm shrink-0 mt-1"
                            style={{ background: COLORS[i % COLORS.length] }}
                          />
                          <span className="truncate flex-1">{c.name}</span>
                          <span className="text-right shrink-0">
                            <span className="tabular-nums font-medium">
                              {formatMxnCents(c.subtotalCents)}
                            </span>
                            {!isHidden && (
                              <span className="text-base-content/55 tabular-nums ml-1">
                                · {pct.toFixed(1)}%
                              </span>
                            )}
                            {c.ivaCents > 0 && (
                              <span className="block text-[10px] text-base-content/50 tabular-nums">
                                IVA {formatMxnCents(c.ivaCents)} = {formatMxnCents(c.totalCents)}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  });
                })()}
              </ul>
            </div>
          </ChartCard>

          {/* ─── Proveedores ─────────────────────────────────────────── */}
          {/* "Top proveedores" (bar) y "Tendencia top 5 proveedores" se
              removieron — la card de distribución (donut) ya cubre el caso
              y las otras eran ruidosas en pantallas estándar. */}

          {supplierDistribution.length > 0 && (
            <ChartCard title="Distribución de gasto por proveedor" subtitle="% del total del rango">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="relative shrink-0" style={{ width: "55%", height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RPieChart>
                      <Pie
                        data={supplierDistribution.filter(
                          (e) => !supplierDistHidden.isHidden(e.name),
                        )}
                        dataKey="subtotalCents"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={100}
                        paddingAngle={2}
                      >
                        {supplierDistribution
                          .filter((e) => !supplierDistHidden.isHidden(e.name))
                          .map((e) => (
                            <Cell key={e.name} fill={supplierColor(e.name)} />
                          ))}
                      </Pie>
                      <Tooltip
                        formatter={(v) => (typeof v === "number" ? formatMxnCents(v) : String(v))}
                      />
                    </RPieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 grid place-items-center pointer-events-none">
                    {(() => {
                      const visible = supplierDistribution.filter(
                        (e) => !supplierDistHidden.isHidden(e.name),
                      );
                      const sub = visible.reduce((s, e) => s + e.subtotalCents, 0);
                      const iva = visible.reduce((s, e) => s + e.ivaCents, 0);
                      return (
                        <div className="text-center leading-tight">
                          <div className="text-[9px] uppercase tracking-wider text-base-content/55 font-semibold">
                            Visible
                          </div>
                          <div className="text-sm font-display font-bold tabular-nums whitespace-nowrap">
                            {formatMxnCents(sub)}
                          </div>
                          {iva > 0 && (
                            <div className="text-[9px] text-base-content/45 tabular-nums whitespace-nowrap mt-0.5">
                              +{formatMxnCents(iva)} IVA
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <ul className="flex-1 text-xs space-y-1 max-h-[260px] overflow-y-auto">
                  {(() => {
                    const visibleSub = supplierDistribution
                      .filter((e) => !supplierDistHidden.isHidden(e.name))
                      .reduce((s, e) => s + e.subtotalCents, 0);
                    return supplierDistribution.map((e) => {
                      const isHidden = supplierDistHidden.isHidden(e.name);
                      const pct = visibleSub > 0 ? (e.subtotalCents / visibleSub) * 100 : 0;
                      return (
                        <li key={e.name}>
                          <button
                            type="button"
                            onClick={() => supplierDistHidden.toggle(e.name)}
                            className="w-full flex items-start gap-2 hover:bg-base-200/50 rounded px-1 py-0.5 text-left cursor-pointer transition-colors"
                            style={{
                              textDecoration: isHidden ? "line-through" : undefined,
                              opacity: isHidden ? 0.45 : 1,
                            }}
                            title={isHidden ? "Mostrar" : "Ocultar del gráfico"}
                          >
                            <span
                              aria-hidden
                              className="size-2.5 rounded-sm shrink-0 mt-1"
                              style={{ background: supplierColor(e.name) }}
                            />
                            <span className="truncate flex-1">{e.name}</span>
                            <span className="text-right shrink-0">
                              <span className="tabular-nums font-medium">
                                {formatMxnCents(e.subtotalCents)}
                              </span>
                              {!isHidden && (
                                <span className="text-base-content/55 tabular-nums ml-1">
                                  · {pct.toFixed(1)}%
                                </span>
                              )}
                              {e.ivaCents > 0 && (
                                <span className="block text-[10px] text-base-content/45 tabular-nums">
                                  +{formatMxnCents(e.ivaCents)} IVA
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    });
                  })()}
                </ul>
              </div>
            </ChartCard>
          )}

          {/* Gastos por método de pago (donut) */}
          {byPaymentMethod.length > 0 && (
            <ChartCard
              title="Gastos por método de pago"
              subtitle="Distribución del flujo de salida"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="relative shrink-0" style={{ width: "55%", height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RPieChart>
                      <Pie
                        data={byPaymentMethod.filter((e) => !paymentMethodHidden.isHidden(e.name))}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={100}
                        paddingAngle={2}
                      >
                        {byPaymentMethod
                          .filter((e) => !paymentMethodHidden.isHidden(e.name))
                          .map((e, i) => (
                            <Cell key={e.name} fill={COLORS[i % COLORS.length]} />
                          ))}
                      </Pie>
                      <Tooltip
                        formatter={(v) => (typeof v === "number" ? formatMxnCents(v) : String(v))}
                      />
                    </RPieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 grid place-items-center pointer-events-none">
                    <div className="text-center leading-tight">
                      <div className="text-[9px] uppercase tracking-wider text-base-content/55 font-semibold">
                        Visible
                      </div>
                      <div className="text-sm font-display font-bold tabular-nums">
                        {formatMxnCents(
                          byPaymentMethod
                            .filter((e) => !paymentMethodHidden.isHidden(e.name))
                            .reduce((s, e) => s + e.value, 0),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <ul className="flex-1 text-xs space-y-1">
                  {(() => {
                    const visibleSum = byPaymentMethod
                      .filter((e) => !paymentMethodHidden.isHidden(e.name))
                      .reduce((s, e) => s + e.value, 0);
                    return byPaymentMethod.map((e, i) => {
                      const isHidden = paymentMethodHidden.isHidden(e.name);
                      const pct = visibleSum > 0 ? (e.value / visibleSum) * 100 : 0;
                      return (
                        <li key={e.name}>
                          <button
                            type="button"
                            onClick={() => paymentMethodHidden.toggle(e.name)}
                            className="w-full flex items-center gap-2 hover:bg-base-200/50 rounded px-1 py-0.5 text-left cursor-pointer transition-colors"
                            style={{
                              textDecoration: isHidden ? "line-through" : undefined,
                              opacity: isHidden ? 0.45 : 1,
                            }}
                          >
                            <span
                              aria-hidden
                              className="size-2.5 rounded-sm shrink-0"
                              style={{ background: COLORS[i % COLORS.length] }}
                            />
                            <span className="truncate flex-1">{e.name}</span>
                            <span className="text-right shrink-0">
                              <span className="tabular-nums font-medium">
                                {formatMxnCents(e.value)}
                              </span>
                              {!isHidden && (
                                <span className="text-base-content/55 tabular-nums ml-1">
                                  · {pct.toFixed(1)}%
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    });
                  })()}
                </ul>
              </div>
            </ChartCard>
          )}

          {/* Margen por proyecto (bar horizontal) */}
          {marginByProject.length > 0 && (
            <ChartCard
              title="Utilidad bruta por proyecto"
              subtitle="Ingresos − gastos del rango"
              pdfKey="margen-proyecto"
            >
              <ResponsiveContainer width="100%" height={Math.max(220, marginByProject.length * 32)}>
                <ComposedChart
                  data={marginByProject}
                  layout="vertical"
                  margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
                  <XAxis
                    type="number"
                    fontSize={11}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis dataKey="name" type="category" fontSize={11} width={140} />
                  <Tooltip
                    formatter={(v) => (typeof v === "number" ? formatMxnCents(v) : String(v))}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="ingresosCents" name="Ingresos" fill={COLORS[0]} />
                  <Bar dataKey="gastosCents" name="Gastos" fill={COLORS[1]} />
                  <Line
                    dataKey="margenCents"
                    name="Utilidad bruta"
                    type="monotone"
                    stroke={COLORS[2]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Top clientes por margen (bar horizontal) */}
          {marginByClient.length > 0 && (
            <ChartCard
              title="Top clientes por margen"
              subtitle="Top 10 · ingresos − gastos atribuidos a la empresa"
            >
              <ResponsiveContainer width="100%" height={Math.max(220, marginByClient.length * 32)}>
                <ComposedChart
                  data={marginByClient}
                  layout="vertical"
                  margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
                  <XAxis
                    type="number"
                    fontSize={11}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis dataKey="name" type="category" fontSize={11} width={140} />
                  <Tooltip
                    formatter={(v) => (typeof v === "number" ? formatMxnCents(v) : String(v))}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="ingresosCents" name="Ingresos" fill={COLORS[0]} />
                  <Bar dataKey="margenCents" name="Utilidad bruta" fill={COLORS[2]} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Histograma de tamaño de transacción */}
          {list.length > 0 && (
            <ChartCard
              title="Tamaño de transacciones"
              subtitle="# de movimientos por rango de monto"
            >
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart
                  data={sizeHistogram}
                  margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    onClick={(p) => histogramHidden.toggle(p.dataKey)}
                    formatter={legendFormatter((k) => histogramHidden.isHidden(k))}
                  />
                  <Bar
                    dataKey="ingresos"
                    name="Ingresos"
                    fill={COLORS[0]}
                    hide={histogramHidden.isHidden("ingresos")}
                  />
                  <Bar
                    dataKey="gastos"
                    name="Gastos"
                    fill={COLORS[1]}
                    hide={histogramHidden.isHidden("gastos")}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {seeAllKind && (
        <TopMovementsDialog
          kind={seeAllKind}
          items={seeAllKind === "gasto" ? topGastos : topIngresos}
          onClose={() => setSeeAllKind(null)}
        />
      )}
    </div>
  );
}

interface TopItem {
  key: string;
  label: string;
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
}

function TopMovementsColumn({
  title,
  items,
  maxValue,
  tone,
  onSeeAll,
}: {
  title: string;
  items: TopItem[];
  maxValue: number;
  tone: "error" | "success";
  onSeeAll: () => void;
}) {
  const colorClass = tone === "error" ? "text-error" : "text-success";
  const bgClass = tone === "error" ? "bg-error" : "bg-success";
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/70">
          {title}
        </h4>
        <button
          type="button"
          onClick={onSeeAll}
          className="text-[11px] text-primary hover:underline"
        >
          Ver todos
        </button>
      </div>
      <ul className="space-y-2">
        {items.map((g) => (
          <li key={g.key} className="flex items-center gap-3 text-sm">
            <div className="flex-1 min-w-0">
              <div className="truncate">{g.label}</div>
              <div className="h-1.5 rounded-full bg-base-200 overflow-hidden mt-1">
                <div
                  className={clsx("h-full", bgClass)}
                  style={{
                    width: `${(g.subtotalCents / (maxValue || 1)) * 100}%`,
                  }}
                />
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={clsx("tabular-nums font-medium", colorClass)}>
                {formatMxnCents(g.subtotalCents)}
              </div>
              {g.ivaCents > 0 && (
                <div className="text-[10px] text-base-content/45 tabular-nums">
                  +{formatMxnCents(g.ivaCents)} IVA
                </div>
              )}
            </div>
          </li>
        ))}
        {items.length === 0 && (
          <li className="text-xs text-base-content/55">
            Sin {tone === "error" ? "gastos" : "ingresos"} en el rango.
          </li>
        )}
      </ul>
    </div>
  );
}

function TopMovementsDialog({
  kind,
  items,
  onClose,
}: {
  kind: "gasto" | "ingreso";
  items: TopItem[];
  onClose: () => void;
}) {
  const tone = kind === "gasto" ? "error" : "success";
  const colorClass = tone === "error" ? "text-error" : "text-success";
  const bgClass = tone === "error" ? "bg-error" : "bg-success";
  const maxValue = items[0]?.subtotalCents ?? 0;
  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-3xl">
        <h3 className="font-display font-semibold text-lg mb-2">
          Top {kind === "gasto" ? "gastos" : "ingresos"}
        </h3>
        <p className="text-xs text-base-content/55 mb-4">
          Agrupados por contraparte / descripción · neto + IVA · ordenados por monto del rango
          activo.
        </p>
        <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
          {items.map((g, i) => (
            <li key={g.key} className="flex items-center gap-3 text-sm">
              <span className="text-xs text-base-content/55 font-mono tabular-nums w-6 shrink-0">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="truncate">{g.label}</div>
                <div className="h-1.5 rounded-full bg-base-200 overflow-hidden mt-1">
                  <div
                    className={clsx("h-full", bgClass)}
                    style={{
                      width: `${(g.subtotalCents / (maxValue || 1)) * 100}%`,
                    }}
                  />
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={clsx("tabular-nums font-medium", colorClass)}>
                  {formatMxnCents(g.subtotalCents)}
                </div>
                {g.ivaCents > 0 && (
                  <div className="text-[10px] text-base-content/45 tabular-nums">
                    +{formatMxnCents(g.ivaCents)} IVA = {formatMxnCents(g.totalCents)}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="modal-action mt-4 pt-3 border-t border-base-300/60">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseAnchor(anchor: string, _mode: RangeMode): Date {
  // anchor puede ser YYYY-MM o YYYY-MM-DD. Devolver el primer día.
  const parts = anchor.split("-").map(Number);
  const y = parts[0] ?? new Date().getFullYear();
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

function resolveRange(
  mode: RangeMode,
  anchor: Date,
  customFrom: string,
  customTo: string,
): { from: string; to: string; label: string; shiftPrev: () => void; shiftNext: () => void } {
  // Las "shift" funciones devuelven a la siguiente unidad temporal del modo.
  // No las uso aquí — el caller las re-implementa via setAnchor + shift abajo.
  // Devolver placeholders, el componente usa los inline.
  if (mode === "personalizado") {
    return {
      from: customFrom,
      to: customTo,
      label: `${customFrom} → ${customTo}`,
      shiftPrev: () => {},
      shiftNext: () => {},
    };
  }
  let from: Date, to: Date, label: string;
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  if (mode === "anual") {
    from = new Date(y, 0, 1);
    to = new Date(y, 11, 31);
    label = String(y);
  } else if (mode === "trimestral") {
    const q = Math.floor(m / 3);
    from = new Date(y, q * 3, 1);
    to = new Date(y, q * 3 + 3, 0);
    label = `Q${q + 1} ${y}`;
  } else if (mode === "mensual") {
    from = new Date(y, m, 1);
    to = new Date(y, m + 1, 0);
    label = anchor.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  } else {
    // semanal: lunes a domingo de la semana del anchor.
    const dow = (anchor.getDay() + 6) % 7; // 0 = lunes
    from = new Date(y, m, anchor.getDate() - dow);
    to = new Date(from.getTime() + 6 * 86400_000);
    const f = from.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
    const t = to.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
    label = `${f} – ${t}`;
  }
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return {
    from: fmt(from),
    to: fmt(to),
    label,
    shiftPrev: () => {},
    shiftNext: () => {},
  };
}

function bucketByPeriod(
  txs: AnalysisTx[],
  from: string,
  to: string,
): {
  data: Array<{ label: string; ingresos: number; gastos: number; acumulado: number }>;
  bucketLabel: string;
} {
  const fromMs = new Date(from + "T00:00:00").getTime();
  const toMs = new Date(to + "T00:00:00").getTime();
  const days = Math.round((toMs - fromMs) / 86400_000) + 1;
  // Elegir granularidad: día si ≤ 31, semana si ≤ 120, mes si más.
  let bucketLabel: string;
  let keyer: (d: Date) => string;
  let labeler: (key: string) => string;
  if (days <= 31) {
    bucketLabel = "día";
    keyer = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    labeler = (k) => k.slice(8);
  } else if (days <= 120) {
    bucketLabel = "semana";
    keyer = (d) => {
      // ISO week-ish: encontrar el lunes de esa semana
      const dow = (d.getDay() + 6) % 7;
      const mon = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow);
      return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, "0")}-${String(mon.getDate()).padStart(2, "0")}`;
    };
    labeler = (k) => `${k.slice(5, 7)}/${k.slice(8)}`;
  } else {
    bucketLabel = "mes";
    keyer = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const mesNames = [
      "ene",
      "feb",
      "mar",
      "abr",
      "may",
      "jun",
      "jul",
      "ago",
      "sep",
      "oct",
      "nov",
      "dic",
    ];
    labeler = (k) => {
      const m = parseInt(k.slice(5, 7), 10);
      return mesNames[m - 1] ?? k;
    };
  }
  const buckets = new Map<string, { ingresos: number; gastos: number }>();
  // Pre-poblar buckets para que las x quepan completas.
  for (let t = fromMs; t <= toMs; t += 86400_000) {
    const d = new Date(t);
    const k = keyer(d);
    if (!buckets.has(k)) buckets.set(k, { ingresos: 0, gastos: 0 });
  }
  for (const tx of txs) {
    const d = new Date(tx.date + "T00:00:00");
    const k = keyer(d);
    const cur = buckets.get(k) ?? { ingresos: 0, gastos: 0 };
    if (tx.kind === "ingreso") cur.ingresos += tx.totalCents / 100;
    else if (tx.kind === "gasto") cur.gastos += tx.totalCents / 100;
    buckets.set(k, cur);
  }
  let acc = 0;
  const data = [...buckets.entries()]
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => {
      acc += v.ingresos - v.gastos;
      return { label: labeler(k), ingresos: v.ingresos, gastos: v.gastos, acumulado: acc };
    });
  return { data, bucketLabel };
}

function topBy(
  txs: AnalysisTx[],
  kind: "ingreso" | "gasto",
  limit: number,
): Array<{
  key: string;
  label: string;
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
  value: number;
}> {
  const map = new Map<string, { sub: number; iva: number }>();
  for (const tx of txs) {
    if (tx.kind !== kind) continue;
    const key = (tx.counterparty || tx.description || "Sin descripción").slice(0, 60);
    const cur = map.get(key) ?? { sub: 0, iva: 0 };
    cur.sub += tx.subtotalCents;
    cur.iva += tx.ivaCents;
    map.set(key, cur);
  }
  return [...map.entries()]
    .map(([key, v]) => ({
      key,
      label: key,
      subtotalCents: v.sub,
      ivaCents: v.iva,
      totalCents: v.sub + v.iva,
      value: v.sub / 100,
    }))
    .toSorted((a, b) => b.subtotalCents - a.subtotalCents)
    .slice(0, limit);
}

/**
 * Mini-tile que muestra el delta vs periodo previo. `better` indica qué
 * dirección es buena: ingresos/margen es "up" (más es mejor), gastos es
 * "down". El color cambia según si el cambio va en la dirección deseada.
 */
function DeltaTile({
  label,
  currentCents,
  previousCents,
  prevRange,
  better,
}: {
  label: string;
  currentCents: number;
  previousCents: number;
  prevRange: { from: string; to: string };
  better: "up" | "down";
}) {
  const diff = currentCents - previousCents;
  const pct = previousCents !== 0 ? (diff / Math.abs(previousCents)) * 100 : 0;
  const noPrev = previousCents === 0;
  const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  const isGood =
    direction === "flat"
      ? null
      : (direction === "up" && better === "up") || (direction === "down" && better === "down");
  const colorClass =
    isGood === null ? "text-base-content/55" : isGood ? "text-success" : "text-error";
  const Arrow = direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : null;
  const rangeLabel = `${shortDate(prevRange.from)} – ${shortDate(prevRange.to)}`;
  return (
    <div className="card-elevated p-3">
      <div className="text-[10px] uppercase tracking-wider text-base-content/55 font-semibold">
        {label}
      </div>
      {noPrev ? (
        <div className="text-sm text-base-content/55 mt-1">Sin periodo previo</div>
      ) : (
        <div className={clsx("flex items-baseline gap-2 mt-1", colorClass)}>
          {Arrow && <Arrow className="size-3.5" />}
          <span className="font-display font-bold text-lg tabular-nums">
            {pct >= 0 ? "+" : ""}
            {pct.toFixed(1)}%
          </span>
          <span className="text-xs tabular-nums opacity-80">
            ({diff >= 0 ? "+" : ""}
            {formatMxnCents(diff)})
          </span>
        </div>
      )}
      <div className="text-[10px] text-base-content/45 mt-0.5">
        <span className="tabular-nums">Antes: {formatMxnCents(previousCents)}</span>
        <span className="ml-1">· {rangeLabel}</span>
      </div>
    </div>
  );
}

const MES_CORTO = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MES_CORTO[m - 1] ?? m}`;
}

/**
 * Card de cumplimiento de facturación: % de movimientos que requieren
 * CFDI y lo tienen (subido o declarado externo). Gauge horizontal.
 */
function CfdiComplianceCard({
  required,
  fulfilled,
  missing,
  pct,
  className,
}: {
  required: number;
  fulfilled: number;
  missing: number;
  pct: number;
  className?: string;
}) {
  const tone =
    required === 0
      ? "text-base-content/55"
      : pct >= 95
        ? "text-success"
        : pct >= 80
          ? "text-warning"
          : "text-error";
  const barColor =
    required === 0 || pct >= 95
      ? "oklch(0.7 0.2 145)"
      : pct >= 80
        ? "oklch(0.78 0.18 60)"
        : "oklch(0.65 0.22 25)";
  return (
    <ChartCard
      title="Cumplimiento de facturación"
      subtitle="CFDI subido o declarado / requieren CFDI"
      className={className}
    >
      {required === 0 ? (
        <p className="text-sm text-base-content/55">
          No hay movimientos que requieran CFDI en el rango.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className={clsx("font-display font-bold text-3xl tabular-nums", tone)}>
              {pct.toFixed(1)}%
            </span>
            <span className="text-xs text-base-content/55 tabular-nums">
              {fulfilled} de {required} movimientos
            </span>
            <span className="ml-auto text-xs tabular-nums flex items-center gap-3">
              <span>
                <span className="text-base-content/55">Con documento </span>
                <span className="font-semibold text-success">{fulfilled}</span>
              </span>
              <span className="text-base-content/30">·</span>
              <span>
                <span className="text-base-content/55">Pendientes </span>
                <span
                  className={clsx(
                    "font-semibold",
                    missing > 0 ? "text-error" : "text-base-content/55",
                  )}
                >
                  {missing}
                </span>
              </span>
            </span>
          </div>
          <div className="h-2 rounded-full bg-base-200 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${pct}%`, background: barColor }}
            />
          </div>
        </div>
      )}
    </ChartCard>
  );
}

function KpiTile({
  label,
  valueCents,
  ivaCents,
  tone,
  icon: Icon,
  hint,
  pctLabel,
  pctTone,
}: {
  label: string;
  valueCents: number;
  /** Si se incluye, valueCents se interpreta como BRUTO (con IVA) y se muestra `Subtotal $X · IVA $Y` debajo. */
  ivaCents?: number;
  tone: "success" | "error" | "warning" | "neutral";
  icon?: typeof BarChart3;
  hint?: string;
  /** Texto opcional (típicamente un %) grande a la derecha del valor. */
  pctLabel?: string;
  /** Color independiente del `pctLabel` (semáforo). Default = `tone`. */
  pctTone?: "success" | "error" | "warning" | "neutral";
}) {
  const colorClass =
    tone === "success"
      ? "text-success"
      : tone === "error"
        ? "text-error"
        : tone === "warning"
          ? "text-warning"
          : "text-base-content";
  const hasIva = ivaCents !== undefined && ivaCents > 0;
  return (
    <div className="card-elevated p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-base-content/55 font-semibold">
        {Icon && <Icon className={clsx("size-3.5", colorClass)} />}
        {label}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <div className={clsx("font-display font-bold text-2xl tabular-nums", colorClass)}>
          {formatMxnCents(valueCents)}
        </div>
        {pctLabel && (
          <div
            className={clsx(
              "font-display font-bold text-2xl tabular-nums shrink-0",
              pctTone
                ? pctTone === "success"
                  ? "text-success"
                  : pctTone === "error"
                    ? "text-error"
                    : pctTone === "warning"
                      ? "text-warning"
                      : "text-base-content"
                : colorClass,
            )}
          >
            {pctLabel}
          </div>
        )}
      </div>
      {hasIva && (
        <div className="text-[11px] text-base-content/50 mt-0.5 tabular-nums">
          Subtotal {formatMxnCents(valueCents - ivaCents)} · IVA {formatMxnCents(ivaCents)}
        </div>
      )}
      {hint && !hasIva && <div className="text-[11px] text-base-content/50 mt-0.5">{hint}</div>}
    </div>
  );
}
